import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useWebSocket, type ServerMessage, type DebugSummary } from "@/hooks/useWebSocket";
import { useIOSKeyboard } from "@/hooks/useIOSKeyboard";
import { useAutoScroll } from "@/hooks/useAutoScroll";
import { api, type Project } from "@/lib/api";
import { relativeTime } from "@/lib/time";
import { Markdown } from "@/components/chat/Markdown";
import { ActivityGroup } from "@/components/chat/ActivityGroup";
import { CopyMessageButton } from "@/components/chat/CopyMessageButton";
import { ScrollToBottom } from "@/components/chat/ScrollToBottom";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { DateSeparator } from "@/components/chat/DateSeparator";
import { ProjectSwitcher } from "@/components/chat/ProjectSwitcher";
import { WidgetRenderer } from "@/components/widgets";
import { ArrowUp, Square, Loader2 } from "lucide-react";

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "error" | "activity" | "widget";
  content: string;
  streaming?: boolean;
  timestamp: Date;
  widgetType?: string;
  widgetData?: Record<string, unknown>;
  widgetActions?: Array<{ id: string; label: string; style?: string }>;
}

type RenderItem =
  | { kind: "message"; msg: ChatMessage }
  | { kind: "activities"; ids: string[]; texts: string[] }
  | { kind: "date"; date: Date; id: string };

function groupMessages(messages: ChatMessage[]): RenderItem[] {
  const items: RenderItem[] = [];
  let activityBatch: { ids: string[]; texts: string[] } | null = null;
  let lastDate: string | null = null;

  for (const msg of messages) {
    if (msg.role === "activity") {
      if (!activityBatch) {
        activityBatch = { ids: [msg.id], texts: [msg.content] };
      } else {
        activityBatch.ids.push(msg.id);
        activityBatch.texts.push(msg.content);
      }
    } else {
      const dayKey = msg.timestamp.toDateString();
      if (dayKey !== lastDate) {
        if (activityBatch) {
          items.push({ kind: "activities", ...activityBatch });
          activityBatch = null;
        }
        items.push({ kind: "date", date: msg.timestamp, id: `date-${dayKey}` });
        lastDate = dayKey;
      }

      if (activityBatch) {
        items.push({ kind: "activities", ...activityBatch });
        activityBatch = null;
      }
      items.push({ kind: "message", msg });
    }
  }

  if (activityBatch) {
    items.push({ kind: "activities", ...activityBatch });
  }

  return items;
}

// ── Debug bar ────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  return `${(n / 1000).toFixed(1)}k`;
}

function DebugBar({ summary }: { summary: DebugSummary }) {
  const [expanded, setExpanded] = useState(false);

  const duration_ms = summary.duration_ms ?? 0;
  const totals = summary.totals;
  const classifier = summary.classifier;
  const agent_loop = summary.agent_loop;
  const lane = summary.lane ?? summary.fast_path_type ?? "unknown";

  const totalTokens = totals?.total_tokens ?? 0;
  const toolCalls = totals?.tool_calls ?? agent_loop?.tool_calls ?? 0;
  const taskLabel = classifier
    ? `${classifier.task_type ?? "?"} · ${classifier.model ?? lane}`
    : lane;

  const debugLines: string[] = [
    `${formatDuration(duration_ms)} · ${formatTokens(totalTokens)} tok · ${toolCalls} tools · ${taskLabel}`,
  ];

  if (expanded) {
    if (classifier) {
      debugLines.push("");
      debugLines.push(`classifier: ${formatDuration(classifier.duration_ms ?? 0)} · ${formatTokens((classifier.input_tokens ?? 0) + (classifier.output_tokens ?? 0))} tok`);
      debugLines.push(`  type: ${classifier.task_type ?? "?"} · model: ${classifier.model ?? "?"} · tools: [${classifier.tool_groups?.join(", ") ?? ""}]`);
    }
    if (agent_loop) {
      debugLines.push("");
      debugLines.push(`agent: ${formatTokens((agent_loop.input_tokens ?? 0) + (agent_loop.output_tokens ?? 0))} tok · ${agent_loop.tool_calls ?? 0} tools · ${agent_loop.iterations ?? 0} iterations`);
      if (agent_loop.files_changed?.length) {
        for (const f of agent_loop.files_changed) {
          debugLines.push(`  ${f.action}: ${f.path}`);
        }
      }
    }
    if (totals) {
      debugLines.push("");
      debugLines.push(`${totals.input_tokens ?? 0} in + ${totals.output_tokens ?? 0} out = ${totals.total_tokens ?? 0} total · ${totals.api_calls ?? 0} API calls`);
    }
    if (summary.governor?.complexity) {
      const cx = summary.governor.complexity;
      debugLines.push("");
      debugLines.push(`complexity: ${cx.score ?? "?"}/100 → ${cx.model ?? "?"} ${cx.phased ? "(phased)" : ""}`);
      if (cx.reasoning) debugLines.push(`  ${cx.reasoning}`);
    }
  }

  return (
    <pre
      className="mt-2 text-[10px] font-mono text-intri-muted/30 cursor-pointer hover:text-intri-muted/50 transition-colors whitespace-pre-wrap select-text leading-relaxed"
      onClick={() => setExpanded((e) => !e)}
    >
      {debugLines.join("\n")}
    </pre>
  );
}

// ── Chat page ────────────────────────────────────────────────

export default function Chat() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const forwardPromptRef = useRef<string | null>(
    (location.state as { forwardPrompt?: string })?.forwardPrompt ?? null
  );

  const [project, setProject] = useState<Project | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const activeSessionRef = useRef<string | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [debugSummaries, setDebugSummaries] = useState<Record<string, DebugSummary>>({});
  const [contextUsage, setContextUsage] = useState<{ percent: number; shouldWarn: boolean } | null>(null);
  const [, setTick] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const streamingIdRef = useRef<string | null>(null);
  const lastAssistantIdRef = useRef<string | null>(null);

  const { isScrolledUp, handleScroll: onAutoScroll, scrollToBottom, resetScroll } =
    useAutoScroll(scrollRef, [messages, debugSummaries]);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  useIOSKeyboard(scrollRef, inputAreaRef, inputRef);

  const handleMessage = useCallback((msg: ServerMessage) => {
    switch (msg.type) {
      case "text_delta": {
        const sid = streamingIdRef.current;
        if (!sid) {
          const newId = crypto.randomUUID();
          streamingIdRef.current = newId;
          setMessages((prev) => [
            ...prev,
            { id: newId, role: "assistant", content: msg.content, streaming: true, timestamp: new Date() },
          ]);
        } else {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === sid ? { ...m, content: m.content + msg.content } : m,
            ),
          );
        }
        break;
      }
      case "text_done": {
        const sid = streamingIdRef.current;
        if (sid) {
          lastAssistantIdRef.current = sid;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === sid ? { ...m, streaming: false } : m,
            ),
          );
          streamingIdRef.current = null;
        }
        setIsBusy(false);
        break;
      }
      case "debug_summary": {
        const aid = lastAssistantIdRef.current;
        if (aid) {
          setDebugSummaries((prev) => ({ ...prev, [aid]: msg as DebugSummary }));
        }
        break;
      }
      case "error": {
        streamingIdRef.current = null;
        setIsBusy(false);
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "error", content: msg.message, timestamp: new Date() },
        ]);
        break;
      }
      case "activity": {
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "activity", content: msg.description, timestamp: new Date() },
        ]);
        break;
      }
      case "widget": {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "widget",
            content: "",
            timestamp: new Date(),
            widgetType: msg.widget_type,
            widgetData: msg.data,
            widgetActions: msg.actions,
          },
        ]);
        setIsBusy(false);
        break;
      }
      case "session_updated":
        break;
      case "session_rotated": {
        activeSessionRef.current = msg.new_session_id;
        break;
      }
      case "greeting": {
        setMessages((prev) => {
          if (prev.length > 0) return prev;
          return [{
            id: crypto.randomUUID(),
            role: "assistant",
            content: msg.content,
            timestamp: new Date(),
          }];
        });
        break;
      }
      case "route_to_project": {
        setTransitioning(true);
        setTimeout(() => {
          navigate(`/chat/${msg.project_id}`, {
            state: { forwardPrompt: msg.forward_prompt },
          });
        }, 400);
        break;
      }
      case "context_usage": {
        setContextUsage({
          percent: msg.percent,
          shouldWarn: msg.percent >= 80,
        });
        break;
      }
    }
  }, []);

  const { state: wsState, agentConnected, connect, sendChat } = useWebSocket({
    projectId: projectId ?? "",
    onMessage: handleMessage,
  });

  useEffect(() => {
    if (!projectId) return;
    api.projects.get(projectId).then(setProject).catch(console.error);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;

    async function init() {
      setLoadingHistory(true);
      try {
        const session = await api.projects.activeSession(projectId!);
        if (cancelled) return;
        activeSessionRef.current = session.id;

        const projectMessages = await api.projects.messages(projectId!, { limit: 50 });
        if (cancelled) return;

        const history: ChatMessage[] = projectMessages
          .reverse()
          .map((m) => {
            if (m.widget_json && m.widget_json.type === "widget") {
              return {
                id: m.id,
                role: "widget" as const,
                content: m.content,
                timestamp: new Date(m.created_at),
                widgetType: m.widget_json.widget_type as string,
                widgetData: m.widget_json.data as Record<string, unknown>,
                widgetActions: m.widget_json.actions as Array<{ id: string; label: string; style?: string }> | undefined,
              };
            }
            return {
              id: m.id,
              role: m.role as "user" | "assistant",
              content: m.content,
              timestamp: new Date(m.created_at),
            };
          });

        setMessages(history);
        setHasMore(projectMessages.length >= 50);
        setDebugSummaries({});
        resetScroll();
      } catch (err) {
        console.error("Failed to initialize chat:", err);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (activeSessionRef.current && wsState === "disconnected") {
      connect();
    }
  }, [activeSessionRef.current, wsState, connect]);

  // Forward a prompt from the concierge into this project chat
  useEffect(() => {
    const prompt = forwardPromptRef.current;
    if (!prompt || !activeSessionRef.current || wsState !== "connected") return;

    // Clear it so it only fires once
    forwardPromptRef.current = null;

    // Small delay to let the WebSocket settle
    const timer = setTimeout(() => {
      setIsBusy(true);
      resetScroll();
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: prompt, timestamp: new Date() },
      ]);
      sendChat(prompt, activeSessionRef.current!);
    }, 300);

    return () => clearTimeout(timer);
  }, [wsState, sendChat, resetScroll]);

  const loadOlderMessages = useCallback(async () => {
    if (!projectId || loadingOlder || !hasMore) return;

    const oldestMsg = messages.find((m) => m.role === "user" || m.role === "assistant");
    if (!oldestMsg) return;

    setLoadingOlder(true);
    try {
      const older = await api.projects.messages(projectId, {
        limit: 30,
        before: oldestMsg.id,
      });

      if (older.length === 0) {
        setHasMore(false);
        return;
      }

      if (older.length < 30) {
        setHasMore(false);
      }

      const olderChat: ChatMessage[] = older
        .reverse()
        .map((m) => {
          if (m.widget_json && m.widget_json.type === "widget") {
            return {
              id: m.id,
              role: "widget" as const,
              content: m.content,
              timestamp: new Date(m.created_at),
              widgetType: m.widget_json.widget_type as string,
              widgetData: m.widget_json.data as Record<string, unknown>,
              widgetActions: m.widget_json.actions as Array<{ id: string; label: string; style?: string }> | undefined,
            };
          }
          return {
            id: m.id,
            role: m.role as "user" | "assistant",
            content: m.content,
            timestamp: new Date(m.created_at),
          };
        });

      const el = scrollRef.current;
      const prevHeight = el?.scrollHeight ?? 0;
      const prevScroll = el?.scrollTop ?? 0;

      setMessages((prev) => [...olderChat, ...prev]);

      requestAnimationFrame(() => {
        if (el) {
          const newHeight = el.scrollHeight;
          el.scrollTop = prevScroll + (newHeight - prevHeight);
        }
      });
    } catch (err) {
      console.error("Failed to load older messages:", err);
    } finally {
      setLoadingOlder(false);
    }
  }, [projectId, messages, loadingOlder, hasMore]);

  function handleScroll() {
    onAutoScroll();

    const el = scrollRef.current;
    if (el && el.scrollTop < 100 && hasMore && !loadingOlder) {
      loadOlderMessages();
    }
  }

  function handleSend() {
    if (isBusy) return;

    const text = input.trim();
    if (!text || !activeSessionRef.current) return;

    setInput("");
    setIsBusy(true);
    resetScroll();

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }

    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: text, timestamp: new Date() },
    ]);

    sendChat(text, activeSessionRef.current);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.35) + "px";
  }

  const renderItems = useMemo(() => groupMessages(messages), [messages]);
  const hasInput = input.trim().length > 0;

  return (
    <div className={`flex h-full flex-col bg-intri-bg animate-fade-in transition-all duration-slow ${
      transitioning ? "opacity-0 scale-[1.02]" : ""
    }`}>
      {/* ── Header ── */}
      <div className="h-3 sm:h-1 shrink-0 safe-top" />
      <header className="flex items-center gap-3 px-5 sm:px-6 py-3">
        <ProjectSwitcher currentProject={project} />

        <div className="flex items-center gap-3 shrink-0">
          {/* Agent status — just a dot, labels hidden on mobile */}
          <div
            className="flex items-center gap-1.5"
            title={agentConnected ? "Agent connected" : "Agent disconnected"}
          >
            <div className={`h-1.5 w-1.5 rounded-full transition-colors duration-slow ${
              agentConnected
                ? "bg-intri-success animate-pulse-soft"
                : "bg-intri-faint"
            }`} />
            <span className={`text-2xs hidden sm:inline transition-colors ${
              agentConnected ? "text-intri-dim" : "text-intri-muted"
            }`}>
              {agentConnected ? "Connected" : "No agent"}
            </span>
          </div>

          {/* WS indicator — only show when not connected */}
          {wsState !== "connected" && (
            <Loader2 size={12} className="text-intri-muted animate-spin" />
          )}
        </div>
      </header>

      {/* Context usage — thin warm line, only when it matters */}
      {contextUsage && contextUsage.percent >= 40 && (
        <div className="h-px mx-4 sm:mx-5 rounded-full overflow-hidden bg-intri-raised">
          <div
            className={`h-full rounded-full transition-all duration-slower ease-out ${
              contextUsage.shouldWarn ? "bg-intri-warning/70" : "bg-intri-accent/30"
            }`}
            style={{ width: `${contextUsage.percent}%` }}
          />
        </div>
      )}

      {/* ── Message area ── */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-5 pt-4 pb-6"
      >
        <div className="max-w-2xl mx-auto space-y-5 min-w-0">

          {loadingOlder && (
            <div className="flex justify-center py-4">
              <Loader2 size={14} className="text-intri-muted animate-spin" />
            </div>
          )}

          {!hasMore && messages.length > 0 && (
            <div className="flex justify-center py-6">
              <p className="text-2xs text-intri-faint">Beginning of conversation</p>
            </div>
          )}

          {loadingHistory && messages.length === 0 && (
            <div className="flex h-full items-center justify-center min-h-[200px]">
              <Loader2 size={16} className="text-intri-muted animate-spin" />
            </div>
          )}

          {!loadingHistory && messages.length === 0 && wsState !== "connected" && (
            <div className="flex h-full items-center justify-center min-h-[200px]">
              <p className="text-sm text-intri-muted animate-fade-in">Connecting...</p>
            </div>
          )}

          {renderItems.map((item) => {
            if (item.kind === "date") {
              return <DateSeparator key={item.id} date={item.date} />;
            }

            if (item.kind === "activities") {
              return (
                <ActivityGroup
                  key={item.ids[0]}
                  activities={item.texts}
                />
              );
            }

            const msg = item.msg;

            return (
              <div
                key={msg.id}
                className={`animate-slide-up ${
                  msg.role === "user" ? "flex flex-col items-end" : ""
                }`}
              >
                {msg.role === "user" ? (
                  /* ── User bubble: faint coral tint ── */
                  <div className="group/msg max-w-[88%] sm:max-w-[75%] md:max-w-[65%]">
                    <div className="rounded-3xl rounded-br-lg bg-[var(--bubble-user)] px-4 py-3 text-sm leading-relaxed text-intri-text break-words overflow-hidden">
                      {msg.content}
                    </div>
                    <span className="text-2xs text-intri-faint mt-1.5 mr-2 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-normal block text-right">
                      {relativeTime(msg.timestamp)}
                    </span>
                  </div>
                ) : msg.role === "error" ? (
                  /* ── Error ── */
                  <div className="rounded-2xl bg-intri-error/[0.06] px-4 py-3 text-sm text-intri-error/80 break-words overflow-hidden">
                    {msg.content}
                  </div>
                ) : msg.role === "widget" && msg.widgetType ? (
                  /* ── Widget ── */
                  <WidgetRenderer
                    widgetType={msg.widgetType}
                    data={msg.widgetData ?? {}}
                    actions={msg.widgetActions}
                    onAction={(actionId) => {
                      if (activeSessionRef.current) {
                        sendChat(actionId, activeSessionRef.current);
                      }
                    }}
                  />
                ) : (
                  /* ── Assistant bubble: faint gold tint ── */
                  <div className="group/msg max-w-full sm:max-w-[90%] md:max-w-[85%]">
                    <div className="rounded-3xl rounded-bl-lg bg-[var(--bubble-assistant)] px-4 py-3 text-intri-text/[0.9] break-words overflow-hidden">
                      <Markdown content={msg.content} />
                      {msg.streaming && (
                        <span className="inline-block w-[2px] h-[1.1em] bg-intri-accent/60 rounded-full ml-0.5 align-middle animate-blink" />
                      )}
                    </div>
                    {!msg.streaming && msg.content.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-1.5 ml-2 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-normal">
                        <span className="text-2xs text-intri-faint">
                          {relativeTime(msg.timestamp)}
                        </span>
                        <CopyMessageButton content={msg.content} />
                        {debugSummaries[msg.id] && (
                          <DebugBar summary={debugSummaries[msg.id]!} />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {isBusy && !streamingIdRef.current && (
            <TypingIndicator />
          )}
        </div>
      </div>

      {/* Scroll-to-bottom */}
      <div className="relative">
        <ScrollToBottom visible={isScrolledUp} onClick={scrollToBottom} />
      </div>

      {/* ── Input area: floating pill, warm and inviting ── */}
      <div ref={inputAreaRef} className="px-4 sm:px-5 pt-2 pb-4 safe-bottom">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-end gap-2 bg-intri-surface/80 rounded-2xl px-3 shadow-intri-md transition-shadow duration-normal focus-within:shadow-intri-glow">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                wsState === "connected"
                  ? agentConnected
                    ? "Message..."
                    : "No agent connected"
                  : "Connecting..."
              }
              disabled={wsState !== "connected"}
              rows={1}
              className="flex-1 resize-none bg-transparent py-3 text-sm text-intri-text placeholder:text-intri-muted/50 focus:outline-none disabled:opacity-40"
            />
            {isBusy ? (
              <button
                className="shrink-0 mb-2 p-1.5 rounded-full text-intri-muted hover:text-intri-error transition-colors"
                title="Stop"
              >
                <Square size={16} />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!hasInput || wsState !== "connected"}
                className={`shrink-0 mb-2 p-1.5 rounded-full transition-all duration-normal ${
                  hasInput
                    ? "text-intri-bg hover:brightness-110 scale-100"
                    : "text-intri-muted/40 scale-95"
                } disabled:opacity-30 disabled:scale-95`}
                style={hasInput ? { background: "var(--brand-gradient)" } : undefined}
                title="Send"
              >
                <ArrowUp size={16} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
